/* Session 147j+16bo (Ship 2) — Read-Only User Dashboard.
 *
 * Mounted at `/dashboard` for ANY authenticated user (admin or user
 * role). Renders the same AlphaScout & LODR components used by the
 * admin panel but passes `readOnly={true}` so they hide every POST/
 * mutation control (Refresh EOD, Run Pipeline Now, Backtest, Force
 * Re-Extract, Recompute-One, Diagnose modals, etc.).
 *
 * Sidebar shows only the 4 items the user is allowed to view:
 *   - AlphaScout (Largecap)
 *   - AlphaScout — Hunter
 *   - AlphaScout — ValueHunter
 *   - LODR Orders
 *
 * No admin section, no research section, no settings. Admins viewing
 * this dashboard still see the same read-only experience; they can
 * jump to `/admin` via a small link in the header if they want write
 * controls.
 */
import React, { useState } from 'react';
import {
  Landmark, TrendingUp, Gem, Newspaper, ShieldCheck, LogOut,
  ExternalLink, HelpCircle, BookOpen, Trophy, Package, Radar,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AlphaScoutDashboard from './alphascout/AlphaScoutDashboard';
import LODROrdersTab from './admin/LODROrdersTab';
import OrderBookLeaderboardTab from './admin/OrderBookLeaderboardTab';  // 147j+16da: consolidated order-flow ranking
import ModelPortfolioTab from './admin/ModelPortfolioTab';  // 147j+17p: Model Portfolio (LLM BUY tracker)
import PreLODRTab from './admin/PreLODRTab';  // 147j+17q: Pre-LODR intelligence
import WelcomeTour from './WelcomeTour';   // 147j+16bp — first-login onboarding
import HelpManual from './HelpManual';     // 147j+16bx — in-app user manual

const NAV_ITEMS = [
  { id: 'alphascout',              label: 'AlphaScout — Largecap',     icon: Landmark },
  { id: 'alphascout-hunter',       label: 'AlphaScout — Mid & Small',  icon: TrendingUp },
  { id: 'alphascout-valuehunter',  label: 'AlphaScout — Value',        icon: Gem },
  { id: 'lodr-orders',             label: 'Order Wins',                icon: Newspaper },
  { id: 'lodr-leaderboard',        label: 'Order Leaderboard',         icon: Trophy },
  { id: 'pre-lodr',                label: 'Pre-LODR',                  icon: Radar },
  { id: 'model-portfolio',         label: 'Model Portfolio',           icon: Package },
  { id: 'help',                    label: 'Help / Manual',             icon: BookOpen },
];

const Sidebar = ({ activeTab, setActiveTab, user, onLogout }) => (
  <aside
    className="w-60 shrink-0 bg-surface border-r border-border/60 flex flex-col h-full overflow-hidden"
    data-testid="user-dashboard-sidebar"
  >
    <div className="px-3 pt-4 pb-3 border-b border-border/40">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-400" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
          AlphaScout + LODR
        </span>
      </div>
      <p className="text-[10px] text-muted-foreground/70 mt-1 px-0.5">
        Read-only research dashboard
      </p>
    </div>

    {/* 147j+16bw — Scrollable nav so the footer (logout + identity)
        stays pinned at the bottom regardless of how many nav items
        exist. Matches AdminDashboard's sidebar layout. */}
    <div className="flex-1 overflow-y-auto min-h-0 px-3 py-4">
      <nav className="space-y-1">
        {NAV_ITEMS.map((it) => {
          const Icon = it.icon;
          const active = activeTab === it.id;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => setActiveTab(it.id)}
              data-testid={`user-nav-${it.id}`}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                active
                  ? 'bg-primary/10 text-primary border border-primary/30'
                  : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{it.label}</span>
            </button>
          );
        })}
      </nav>
    </div>

    {/* 147j+16bw — Footer with identity + Logout. Pinned to the bottom
        of the sidebar (shrink-0) with rose-red text, matching the
        AdminDashboard pattern so the user can always find Logout in
        the same spot whether they're on /dashboard or /admin. */}
    <div className="border-t border-border shrink-0">
      {user?.email && (
        <div
          className="px-4 py-2 text-[10px] text-muted-foreground font-mono truncate"
          title={user.email}
        >
          <span className="text-muted-foreground/60">signed in as</span>{' '}
          <span className="text-foreground/80">{user.email}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onLogout}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 transition-colors border-t border-border/40"
        data-testid="user-dashboard-sidebar-logout"
        title="Sign out"
      >
        <LogOut className="w-4 h-4" />
        <span>Logout</span>
      </button>
      <div className="px-4 py-2 text-[10px] text-muted-foreground/60 font-mono border-t border-border/40">
        v16bo · read-only
      </div>
    </div>
  </aside>
);

const Header = ({ user, isAdmin, onReplayTour }) => (
  <header
    className="border-b border-border/60 bg-surface px-5 py-3 flex items-center justify-between shrink-0"
    data-testid="user-dashboard-header"
  >
    <div>
      <h1 className="text-base font-semibold text-foreground">
        Research Dashboard
      </h1>
      <p className="text-[11px] text-muted-foreground">
        AlphaScout signals · LODR orders · refreshed daily
      </p>
    </div>
    <div className="flex items-center gap-3">
      <div className="text-right hidden sm:block">
        <div className="text-xs text-foreground font-medium">
          {user?.name || user?.email || 'User'}
        </div>
        <div className="text-[10px] text-muted-foreground">{user?.email}</div>
      </div>
      <button
        type="button"
        onClick={onReplayTour}
        title="Replay welcome tour"
        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground border border-border rounded px-2 py-1"
        data-testid="user-dashboard-tour-btn"
      >
        <HelpCircle className="w-3 h-3" /> Tour
      </button>
      {isAdmin && (
        <a
          href="/admin"
          className="flex items-center gap-1 text-[11px] text-primary hover:underline border border-primary/30 rounded px-2 py-1"
          data-testid="user-dashboard-admin-link"
        >
          Admin Panel <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  </header>
);

const UserDashboard = () => {
  const { user, isAdmin, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('alphascout');
  // 147j+16bp — Manual replay state. When true, WelcomeTour mounts in
  // forced-open mode irrespective of the localStorage `seen` flag.
  // After it closes (writes seen='1' again), reset to false.
  const [forceTour, setForceTour] = useState(false);

  const handleReplayTour = () => {
    try { localStorage.removeItem('dashboard_tour_seen'); } catch (_) { /* ignore */ }
    setForceTour(true);
    // Reset after a tick so future renders don't re-trigger.
    setTimeout(() => setForceTour(false), 100);
  };

  return (
    // 147j+16bw — `h-screen overflow-hidden` so the inner <main>'s
    // `overflow-y-auto` actually engages. Previously `min-h-screen`
    // only set a minimum height — the page grew with content and
    // main never scrolled (no inner scroll area at all).
    <div className="h-screen overflow-hidden flex bg-background" data-testid="user-dashboard">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        onLogout={logout}
      />
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <Header
          user={user}
          isAdmin={isAdmin}
          onReplayTour={handleReplayTour}
        />
        <main className="flex-1 overflow-y-auto p-4 min-h-0">
          {activeTab === 'alphascout' && (
            <AlphaScoutDashboard profile="largecap" readOnly />
          )}
          {activeTab === 'alphascout-hunter' && (
            <AlphaScoutDashboard profile="midsmall" readOnly />
          )}
          {activeTab === 'alphascout-valuehunter' && (
            <AlphaScoutDashboard profile="valuehunter" readOnly />
          )}
          {activeTab === 'lodr-orders' && <LODROrdersTab readOnly />}
          {activeTab === 'lodr-leaderboard' && <OrderBookLeaderboardTab />}
          {activeTab === 'model-portfolio' && <ModelPortfolioTab readOnly />}
          {activeTab === 'pre-lodr' && <PreLODRTab readOnly />}
          {activeTab === 'help' && <HelpManual />}
        </main>
      </div>
      {/* First-login welcome tour. Renders only when `dashboard_tour_seen`
          is not "1" OR the user clicked the header Tour button. */}
      <WelcomeTour forceOpen={forceTour} />
    </div>
  );
};

export default UserDashboard;
